import { useRef } from 'react';
import type {
  ControlDiagramNode,
  DiagramConfiguration,
  EscalationFactorBarrierDiagramNode,
  EscalationFactorDiagramNode,
} from '../@types/diagram';
import { useGetBowtieByIdQuery } from '../api/enhanced/enhanced-bowtie-api';
import type { Bowtie, Control, ControlWithId } from '../api/generated/bowtie-api';
import { useDiagramContext } from '../context/diagram.context';
import { CriticalControlEnum } from '../helpers/node-util';

const useSavedDiagram = () => {
  const diagramDataRef = useRef<DiagramConfiguration | undefined>(undefined);
  const { diagramId } = useDiagramContext();

  const { data, isFetching, isError } = useGetBowtieByIdQuery({ bowtieId: diagramId! }, { skip: !diagramId });

  if (diagramId && data && !isFetching && !isError && !diagramDataRef.current) {
    diagramDataRef.current = buildDiagramData(data);
  }

  if (isError) {
    diagramDataRef.current = undefined;
  }

  return {
    isFetching,
    isError,
    diagramData: diagramDataRef.current,
  };
};

export default useSavedDiagram;

/**
 * Builds a diagram configuration from a Bowtie data structure.
 *
 * This function transforms a Bowtie risk assessment data structure into a diagram
 * configuration that can be used to render a bow-tie diagram. It generates unique IDs
 * for each node and maps the data maintaining the bow-tie structure with causes,
 * consequences, and their respective controls.
 *
 * @param data - The Bowtie data structure containing risk assessment information
 * @returns A DiagramConfiguration object containing the structured diagram data
 *
 * @example
 * const bowtieData = {
 *   risk: "System Failure",
 *   hazard: "Equipment Malfunction",
 *   causes: [...],
 *   consequences: [...]
 * };
 * const diagramConfig = buildDiagramData(bowtieData);
 *
 * @remarks
 * - Generates unique IDs using crypto.randomUUID()
 * - Maps controls maintaining critical/non-critical status
 * - Preserves global control indicators
 * - Includes metadata for AI-generated controls
 */
export function buildDiagramData(data: Bowtie): DiagramConfiguration {
  const diagramData: DiagramConfiguration = {
    mue: { id: crypto.randomUUID() },
    causes: [],
    consequences: [],
  };

  //mue
  diagramData.mue = {
    ...diagramData.mue,
    label: data.risk,
  };

  // hazard
  diagramData.hazard = {
    id: crypto.randomUUID(),
    label: data.hazard,
  };

  // causes and preventative controls
  diagramData.causes =
    data.causes?.map((cause) => {
      const controls =
        cause.controls?.map((control) => {
          const criticalOrNonCritical = control.isCritical ? CriticalControlEnum.CRITICAL : undefined;
          const id = control.id ?? crypto.randomUUID();
          return {
            id,
            label: control.name,
            criticalControlType: criticalOrNonCritical,
            _metadata: {
              aiData: mapSavedDiagramControlToAIGeneratedControlWithId(id, control),
            },
          } as ControlDiagramNode;
        }) ?? [];

      return {
        id: crypto.randomUUID(),
        label: cause.name,
        controls,
      };
    }) ?? [];

  // consequences and mitigating controls
  diagramData.consequences =
    data.consequences?.map((consequence) => {
      const controls =
        consequence.controls?.map((control) => {
          const criticalOrNonCritical = control.isCritical ? CriticalControlEnum.CRITICAL : undefined;
          const id = control.id ?? crypto.randomUUID();
          return {
            id,
            label: control.name,
            criticalControlType: criticalOrNonCritical,
            _metadata: {
              aiData: mapSavedDiagramControlToAIGeneratedControlWithId(id, control),
            },
          } as ControlDiagramNode;
        }) ?? [];

      return {
        id: crypto.randomUUID(),
        label: consequence.name,
        controls,
      };
    }) ?? [];

  // escalation factors (link back to their parent control via controlId → controlNodeId);
  // EFs whose controlId does not resolve to a loaded control are dropped.
  if (data.escalationFactors && data.escalationFactors.length > 0) {
    const allControls = [
      ...diagramData.causes.flatMap((cause) => cause.controls),
      ...diagramData.consequences.flatMap((consequence) => consequence.controls),
    ];

    diagramData.escalationFactors = data.escalationFactors
      .map((efDto) => {
        if (!efDto.controlId) return undefined;

        const control = allControls.find((c) => c.id === efDto.controlId);
        if (!control) return undefined;

        const escalationFactorNodeId = efDto.id ?? crypto.randomUUID();

        const barriers = (efDto.escalationFactorBarriers ?? []).map(
          (barrierDto) =>
            ({
              id: barrierDto.id ?? crypto.randomUUID(),
              label: barrierDto.name,
              escalationFactorNodeId,
              controlNodeId: control.id,
            }) as EscalationFactorBarrierDiagramNode
        );

        return {
          id: escalationFactorNodeId,
          label: efDto.name,
          barriers,
          controlNodeId: control.id,
        } as EscalationFactorDiagramNode;
      })
      .filter((ef): ef is EscalationFactorDiagramNode => ef !== undefined);
  }

  return diagramData;
}

const mapSavedDiagramControlToAIGeneratedControlWithId = (id: string, control: Control): ControlWithId => {
  return {
    id,
    name: control.name!,
    category: control.category!,
    type: control.type!,
    controlType: control.controlType!,
    isCritical: Boolean(control.isCritical),
    isInitial: Boolean(control.isInitial),
    isControlIssues: false, // missing in the Bowtie service api
  };
};
